import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createStorageSlot } from '@gonfalon/browser-storage/createStorageSlot';
import { useStorageSlot } from '@gonfalon/browser-storage/useStorageSlot';
import { sidebarMetricInstrumentation } from '@gonfalon/dogfood-flags/dogfood-flags';
import { useMediaQuery } from '@gonfalon/dom/useMediaQuery';
import { useMediaQueryListener } from '@gonfalon/dom/useMediaQueryListener';
import { createStateChartReducer } from '@gonfalon/react/createStateChartReducer';
import { useHotkey } from '@gonfalon/react/useHotkey';

import { type SidebarToggleTrigger, trackSidebarSessionDuration, trackSidebarToggled } from '../analytics';
import type { KeyboardShortcut } from '../keyboardShortcuts';

export type SidebarToggleMeta = { trigger?: SidebarToggleTrigger; width?: number };

type VisibilityState = 'open' | 'closed' | 'auto-closed' | 'route-closed';

type VisibilityAction =
  | 'toggle'
  | 'minimized'
  | 'maximized'
  | 'route-closed'
  | 'route-released'
  | 'route-released-minimized';

const computeNextVisibility = createStateChartReducer<VisibilityState, VisibilityAction>({
  open: {
    transitions: {
      toggle: 'closed',
      minimized: 'auto-closed',
      'route-closed': 'route-closed',
    },
  },
  closed: {
    transitions: {
      toggle: 'open',
      minimized: 'closed',
      maximized: 'closed',
    },
  },
  'auto-closed': {
    transitions: {
      toggle: 'open',
      minimized: 'auto-closed',
      maximized: 'open',
      'route-closed': 'route-closed',
    },
  },
  'route-closed': {
    transitions: {
      toggle: 'open',
      'route-released': 'open',
      'route-released-minimized': 'auto-closed',
    },
  },
});

const railStorage = createStorageSlot('chrome-main-sidebar-rail');

type UseSidebarStateProps = {
  breakpoint: `${number}rem`;
  toggleShortcut: KeyboardShortcut;
  preferClosedByDefault?: boolean;
  storageKey?: string;
  /** Identifies this sidebar in analytics events (e.g. 'project-entity', 'main-nav'). */
  sidebarId?: string;
  collapseToRail?: boolean;
  forceExpanded?: boolean;
};

export function useSidebarState({
  breakpoint,
  toggleShortcut,
  preferClosedByDefault = false,
  storageKey,
  sidebarId,
  collapseToRail = false,
  forceExpanded = false,
}: UseSidebarStateProps) {
  const mediaQuery = `(max-width: ${breakpoint})`;
  const isSmall = useMediaQuery(mediaQuery);

  const persistenceEnabled = Boolean(storageKey);

  const [rawPersisted, slot] = useStorageSlot(storageKey ?? 'chrome-sidebar-noop', {
    isEnabled: persistenceEnabled,
  });

  const [visibility, setVisibility] = useState<VisibilityState>(() => {
    if (collapseToRail) {
      if (railStorage.get() === 'rail') {
        return 'closed';
      }
      if (preferClosedByDefault) {
        return 'route-closed';
      }
      return isSmall ? 'auto-closed' : 'open';
    }

    if (persistenceEnabled && rawPersisted === 'closed') {
      return 'closed';
    }

    if (preferClosedByDefault) {
      return 'route-closed';
    }

    return isSmall ? 'auto-closed' : 'open';
  });

  const isRailSuppressed = collapseToRail && forceExpanded;
  const isForcedExpanded = isRailSuppressed && !isSmall && visibility !== 'auto-closed';
  const isVisible = visibility === 'open' || isForcedExpanded;
  const isRail =
    collapseToRail && !isSmall && !isRailSuppressed && (visibility === 'closed' || visibility === 'route-closed');
  const focusHandoffRef = useRef(false);

  // Analytics: see ../analytics trackSidebar* helpers. The sidebar is a shared primitive used
  // across flags/segments/experiments, so instrumenting open/close here gives every product area
  // coverage for free (Option A in the Sidebar Metric Instrumentation proposal). `trigger` and
  // `width` are supplied by the caller via toggle(meta) since they live outside this hook.
  const [mountedAt] = useState(() => Date.now());
  const openedAtRef = useRef<number | null>(isVisible ? mountedAt : null);
  const prevVisibleRef = useRef(isVisible);
  const pendingTriggerRef = useRef<SidebarToggleTrigger>('button');
  const pendingWidthRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    // Gate the whole effect on the flag: when instrumentation is off this hook does no analytics
    // work at all (per review on PR #65480).
    if (!sidebarMetricInstrumentation()) {
      return;
    }
    if (prevVisibleRef.current === isVisible) {
      return;
    }
    prevVisibleRef.current = isVisible;

    const page = window.location.pathname;
    const trigger = pendingTriggerRef.current;
    const width = pendingWidthRef.current;

    if (isVisible) {
      openedAtRef.current = Date.now();
      trackSidebarToggled({ action: 'opened', trigger, page, sidebar: sidebarId, width });
    } else {
      trackSidebarToggled({ action: 'closed', trigger, page, sidebar: sidebarId, width });
      if (openedAtRef.current !== null) {
        trackSidebarSessionDuration({
          durationMs: Math.round(Date.now() - openedAtRef.current),
          page,
          sidebar: sidebarId,
        });
        openedAtRef.current = null;
      }
    }

    // Reset to defaults so a subsequent state change without explicit meta is attributed correctly.
    pendingTriggerRef.current = 'button';
    pendingWidthRef.current = undefined;
  }, [isVisible, sidebarId]);

  const prevPreferClosedByDefaultRef = useRef(preferClosedByDefault);

  useLayoutEffect(() => {
    if (prevPreferClosedByDefaultRef.current === preferClosedByDefault) {
      return;
    }
    prevPreferClosedByDefaultRef.current = preferClosedByDefault;

    let action: VisibilityAction = 'route-closed';
    if (!preferClosedByDefault) {
      action = isSmall ? 'route-released-minimized' : 'route-released';
    }

    pendingTriggerRef.current = 'navigation';
    setVisibility((prev) => computeNextVisibility(prev, action));
  }, [preferClosedByDefault, isSmall]);

  useMediaQueryListener(mediaQuery, (m) => {
    pendingTriggerRef.current = 'responsive';
    setVisibility((prev) => computeNextVisibility(prev, m ? 'minimized' : 'maximized'));
  });

  const updateCache = useCallback(
    (next: VisibilityState) => {
      if (collapseToRail) {
        if (isSmall) {
          return;
        }
        if (next === 'closed') {
          railStorage.set('rail');
        } else if (next === 'open') {
          railStorage.remove();
        }
        return;
      }
      if (!persistenceEnabled) {
        return;
      }
      if (next === 'closed') {
        slot.set('closed');
      } else {
        slot.remove();
      }
    },
    [collapseToRail, isSmall, persistenceEnabled, slot],
  );

  const toggle = useCallback(
    (meta?: SidebarToggleMeta) => {
      if (isForcedExpanded) {
        return;
      }
      if (meta?.trigger) {
        pendingTriggerRef.current = meta.trigger;
      }
      if (meta?.width !== undefined) {
        pendingWidthRef.current = meta.width;
      }
      setVisibility((prev) => {
        let next = computeNextVisibility(prev, 'toggle');
        if (collapseToRail && isSmall && next === 'closed') {
          next = 'auto-closed';
        }
        if (collapseToRail && next !== prev) {
          const nav = document.getElementById('sidebar-navigation');
          focusHandoffRef.current = !!nav?.contains(document.activeElement);
        } else if (next === prev) {
          focusHandoffRef.current = false;
        }
        if (!isRailSuppressed) {
          updateCache(next);
        }
        return next;
      });
    },
    [collapseToRail, isForcedExpanded, isRailSuppressed, isSmall, updateCache],
  );

  const consumeFocusHandoff = useCallback(() => {
    const shouldMove = focusHandoffRef.current;
    focusHandoffRef.current = false;
    return shouldMove;
  }, []);

  useHotkey(toggleShortcut, () => toggle({ trigger: 'keyboard' }));

  return useMemo(
    () => ({ isVisible, isRail, isForcedExpanded, toggle, consumeFocusHandoff }),
    [isVisible, isRail, isForcedExpanded, toggle, consumeFocusHandoff],
  );
}
